import React, { useCallback, useRef, useState, memo } from 'react';
import { Plus, Bot, LayoutGrid, Command, PanelLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMobile } from '../../contexts/MobileContext';
import ProfileButton from '../profile/ProfileButton';
import TooltipPortal from '../common/tooltip/TooltipPortal';

import styles from './styles/Sidebar.module.css';

const Sidebar = ({ children, handleNewChat = null, newChatType = 'user' }) => {
    const { isSidebarCollapsed, toggleSidebar, page, setPage, closeSidebar, sidebarRef } = useSidebar();
    const { isMobile, isPwa } = useMobile();
    const containerRef = useRef(null);
    const navigate = useNavigate();
    const [hoveredNewChat, setHoveredNewChat] = useState(false);

    const handleClickNewChat = useCallback(() => {
        if (handleNewChat) {
            handleNewChat();
            // Close sidebar on mobile when creating a new chat
            if (isMobile) {
                closeSidebar();
            }
        }
    }, [handleNewChat, isMobile, closeSidebar]);

    // Clone children and pass containerRef as a prop
    const childrenWithRef = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { containerRef });
        }
        return child;
    });

    const handleClickMainButton = useCallback((e, newPage) => {
        e.preventDefault();
        if (page !== newPage) {
            setPage(newPage);
            navigate(`/${newPage}`);

            // Close sidebar on mobile when navigating
            if (isMobile) {
                closeSidebar();
            }
        }
    }, [navigate, setPage, page, isMobile, closeSidebar]);

    const renderMainButton = useCallback((newPage, icon, text) => {
        return (
            <a
                onClick={e => handleClickMainButton(e, newPage)}
                href={`/${newPage}`}
                className={`${styles.mainButton} ${page === newPage ? styles.selected : ''}`}
            >
                {icon}
                {text}
            </a>
        );
    }, [handleClickMainButton, page]);

    const newChatTooltip = newChatType === 'agent' ? 'New chat with this agent' : 'New chat';

    return <>
        {/* Dark overlay when open (on mobile) */}
        {isMobile && !isSidebarCollapsed && (
            <div
                className={`${styles.overlay} ${!isSidebarCollapsed ? styles.overlayVisible : ''}`}
                onClick={closeSidebar}
            />
        )}

        <div
            ref={sidebarRef}
            className={`${styles.sidebar} ${!isSidebarCollapsed ? styles.sidebarOpen : ''} ${isPwa ? styles.sidebarPwa : ''}`}
        >
            <div className={styles.header} style={isSidebarCollapsed ? { width: '0' } : {}}>
                <button onClick={toggleSidebar} className={styles.menuButton} aria-label="Collapse sidebar">
                    <PanelLeft />
                </button>
                {handleNewChat && (
                    <TooltipPortal tooltip={newChatTooltip} show={hoveredNewChat}>
                        <button
                            onClick={handleClickNewChat}
                            className={styles.menuButton}
                            aria-label="Create new chat"
                            onMouseEnter={() => setHoveredNewChat(true)}
                            onMouseLeave={() => setHoveredNewChat(false)}
                        >
                            <Plus />
                        </button>
                    </TooltipPortal>
                )}
            </div>

            <div className={`${styles.allButtons} ${isSidebarCollapsed ? styles.collapsed : ''}`}>
                <div className={styles.mainButtons}>
                    {renderMainButton('chats', <Bot />, 'Chats')}
                    {renderMainButton('store', <LayoutGrid />, 'Store')}
                    {renderMainButton('agents', <Command />, 'Agents')}
                </div>

                <div className={styles.listBlock} ref={containerRef}>
                    {childrenWithRef}
                </div>

                <ProfileButton />
            </div>
        </div>
    </>;
};

export default memo(Sidebar);
